"use client";

import { useRef, useMemo, useState, useEffect } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Typewriter hook
function useTypewriter(text: string, speed: number = 50, delay: number = 0) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsComplete(false);

    const startTimeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayedText, isComplete };
}

// Count up animation hook
function useCountUp(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Ease out quad
      const easeOut = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [hasStarted, end, duration]);

  return { count, ref };
}

// Animated stat component
function AnimatedStat({ value, label }: { value: string; label: string }) {
  const numericValue = parseInt(value.replace(/\D/g, ""));
  const suffix = value.replace(/[0-9]/g, "");
  const { count, ref } = useCountUp(numericValue, 1500);

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-semibold mb-2 text-white">
        {count}{suffix}
      </div>
      <div className="text-white/70 text-sm">
        {label}
      </div>
    </div>
  );
}

// Hero section with typewriter effect
function HeroSection({ displayedText, isComplete }: { displayedText: string; isComplete: boolean }) {
  const fullText = "AI-native software\ndelivery for\nmodern enterprises";

  // Determine which line the cursor is on
  const lines = displayedText.split("\n");
  const currentLineIndex = lines.length - 1;

  const fullLines = fullText.split("\n");
  const line1Full = fullLines[0];
  const line2Full = fullLines[1];

  // Get what's typed for each line
  const line1 = lines[0] || "";
  const line2 = lines[1] || "";
  const line3 = lines[2] || "";

  const cursor = (
    <span
      className={`inline-block w-[3px] h-[0.9em] bg-[#ff6900] ml-0.5 align-middle ${
        isComplete ? "opacity-0" : "animate-pulse"
      }`}
    />
  );

  return (
    <section className="min-h-[45vh] md:min-h-[65vh] flex items-center px-6 pt-32 md:pt-20 pb-4 md:pb-8 relative">
      <div className="max-w-6xl mx-auto w-full">
        <div className="max-w-2xl">
          <div
            className={`hidden md:flex items-center gap-3 mb-8 transition-opacity duration-500 ${
              isComplete ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-[#ff6900]" />
            <span className="text-sm text-[#1a1a1a]/70 tracking-wide">
              Enterprise Software Agency
            </span>
          </div>

          {/* Reserve space for all 3 lines to prevent layout shift */}
          <div className="min-h-[9.45rem] md:min-h-[11.8rem] lg:min-h-[14.2rem] mb-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight">
              <span className="block">
                {line1}
                {currentLineIndex === 0 && !isComplete && cursor}
              </span>
              {displayedText.length > line1Full.length && (
                <span className="block text-[#1a1a1a]">
                  {line2}
                  {currentLineIndex === 1 && !isComplete && cursor}
                </span>
              )}
              {displayedText.length > line1Full.length + line2Full.length + 1 && (
                <span className="block text-[#ff6900]">
                  {line3}
                  {currentLineIndex === 2 && !isComplete && cursor}
                </span>
              )}
            </h1>
          </div>

          <p
            className={`text-xl text-[#1a1a1a]/70 max-w-xl mb-6 md:mb-12 leading-relaxed transition-all duration-700 ${
              isComplete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            We design, build, and modernize enterprise software across AI, data, integrations, UX and UI design, content platforms, and application development.
          </p>

          <div
            className={`hidden md:flex flex-col sm:flex-row gap-3 transition-all duration-700 delay-200 ${
              isComplete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white font-medium hover:opacity-90 transition-opacity"
            >
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/work"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-[#1a1a1a]/10 text-[#1a1a1a] font-medium hover:bg-white/50 bg-white transition-colors"
            >
              View our work
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// Subtle floating geometry (HIDDEN - kept for potential revert)
function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);
  const mesh3Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) {
      groupRef.current.rotation.y = t * 0.02;
    }
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.1;
      mesh1Ref.current.rotation.z = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.3) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.08;
      mesh2Ref.current.position.y = Math.sin(t * 0.4 + 1) * 0.2;
    }
    if (mesh3Ref.current) {
      mesh3Ref.current.rotation.x = t * 0.06;
      mesh3Ref.current.rotation.y = t * 0.04;
      mesh3Ref.current.position.y = Math.sin(t * 0.35 + 2) * 0.25;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Main octahedron - larger and more visible */}
      <mesh ref={mesh1Ref} position={[2, 0, 0]}>
        <octahedronGeometry args={[2.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.4} transparent />
      </mesh>

      {/* Secondary icosahedron */}
      <mesh ref={mesh2Ref} position={[-2, 1, -1]}>
        <icosahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>

      {/* Tertiary dodecahedron */}
      <mesh ref={mesh3Ref} position={[0, -1.5, -2]}>
        <dodecahedronGeometry args={[1.2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.25} transparent />
      </mesh>
    </group>
  );
}

// Subtle grid of dots (HIDDEN - kept for potential revert)
function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);

  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.z = state.clock.elapsedTime * 0.01;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
        />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

// 3D Wireframe Tree with line-drawing animation and neural firing effect
function WireframeTree() {
  const groupRef = useRef<THREE.Group>(null);
  const linesRef = useRef<THREE.LineSegments>(null);
  const firingPointsRef = useRef<THREE.Points>(null);
  const [drawProgress, setDrawProgress] = useState(0);
  const totalVertices = useRef(0);
  const mousePos = useRef({ x: 0, y: 0 });
  const targetRotation = useRef({ x: 0, y: 0 });
  const vertexPositions = useRef<number[]>([]);
  const firingData = useRef<{ x: number; y: number; z: number; startTime: number; duration: number }[]>([]);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mousePos.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Create line geometry for tree
  const lineGeometry = useMemo(() => {
    const positions: number[] = [];
    const vertexSet = new Set<string>();
    const uniqueVerts: number[] = [];

    const addGeometryEdges = (
      geo: THREE.BufferGeometry,
      offsetX: number,
      offsetY: number,
      offsetZ: number,
    ) => {
      const edges = new THREE.EdgesGeometry(geo);
      const posAttr = edges.getAttribute("position");
      for (let i = 0; i < posAttr.count; i++) {
        const x = posAttr.getX(i) + offsetX;
        const y = posAttr.getY(i) + offsetY;
        const z = posAttr.getZ(i) + offsetZ;
        positions.push(x, y, z);

        const key = `${x.toFixed(2)},${y.toFixed(2)},${z.toFixed(2)}`;
        if (!vertexSet.has(key)) {
          vertexSet.add(key);
          uniqueVerts.push(x, y, z);
        }
      }
      edges.dispose();
    };

    const canopyData = [
      { pos: [0, 2.2, 0], size: 1.3 },
      { pos: [0.2, 2.0, 0.7], size: 0.9 },
      { pos: [-0.9, 1.7, 0.5], size: 1.0 },
      { pos: [1.0, 1.8, -0.3], size: 1.1 },
      { pos: [0.8, 1.3, 0.6], size: 0.95 },
      { pos: [0, 1.2, -0.7], size: 1.2 },
      { pos: [-1.3, 1.1, 0.2], size: 1.2 },
      { pos: [1.3, 1.0, 0.3], size: 1.1 },
      { pos: [-0.5, 0.9, 0.9], size: 1.0 },
      { pos: [0.2, 0.6, 0.8], size: 0.95 },
      { pos: [-1.0, 0.4, 0.5], size: 1.05 },
      { pos: [1.1, 0.5, -0.2], size: 1.0 },
    ];

    canopyData.forEach(({ pos, size }) => {
      const sphereGeo = new THREE.IcosahedronGeometry(size, 1);
      addGeometryEdges(sphereGeo, pos[0], pos[1], pos[2]);
      sphereGeo.dispose();
    });

    const trunkGeo = new THREE.CylinderGeometry(0.15, 0.25, 1.2, 8);
    addGeometryEdges(trunkGeo, 0, -0.8, 0);
    trunkGeo.dispose();

    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    totalVertices.current = positions.length / 3;
    vertexPositions.current = uniqueVerts;

    return lineGeo;
  }, []);

  // Dynamic geometry for firing points - only contains active fires
  const firingGeometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    // Pre-allocate for max 50 simultaneous fires
    const positions = new Float32Array(50 * 3);
    const opacities = new Float32Array(50);
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("opacity", new THREE.BufferAttribute(opacities, 1));
    geo.setDrawRange(0, 0);
    return geo;
  }, []);

  // Draw-in animation
  useEffect(() => {
    const duration = 3000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      setDrawProgress(eased);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    const timeout = setTimeout(() => requestAnimationFrame(animate), 300);
    return () => clearTimeout(timeout);
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const now = Date.now();

    if (groupRef.current) {
      targetRotation.current.y = t * 0.05 + mousePos.current.x * 0.5;
      targetRotation.current.x = mousePos.current.y * 0.3;
      groupRef.current.rotation.y += (targetRotation.current.y - groupRef.current.rotation.y) * 0.05;
      groupRef.current.rotation.x += (targetRotation.current.x - groupRef.current.rotation.x) * 0.05;
    }

    if (linesRef.current && lineGeometry) {
      const vertexCount = Math.floor(totalVertices.current * drawProgress);
      lineGeometry.setDrawRange(0, vertexCount);
    }

    // Neural firing - only render active fires
    if (firingPointsRef.current && drawProgress > 0.5) {
      const verts = vertexPositions.current;
      const numVerts = verts.length / 3;

      // Randomly trigger new firings
      if (Math.random() < 0.15 && firingData.current.length < 50) {
        const idx = Math.floor(Math.random() * numVerts);
        firingData.current.push({
          x: verts[idx * 3],
          y: verts[idx * 3 + 1],
          z: verts[idx * 3 + 2],
          startTime: now,
          duration: 200 + Math.random() * 400,
        });
      }

      // Update firing geometry with only active fires
      const posAttr = firingGeometry.getAttribute("position") as THREE.BufferAttribute;
      const positions = posAttr.array as Float32Array;

      let activeCount = 0;
      firingData.current = firingData.current.filter((fire) => {
        const elapsed = now - fire.startTime;
        if (elapsed > fire.duration) return false;

        if (activeCount < 50) {
          positions[activeCount * 3] = fire.x;
          positions[activeCount * 3 + 1] = fire.y;
          positions[activeCount * 3 + 2] = fire.z;
          activeCount++;
        }
        return true;
      });

      posAttr.needsUpdate = true;
      firingGeometry.setDrawRange(0, activeCount);
    }
  });

  return (
    <group ref={groupRef} position={[1.5, -0.2, 0]} scale={0.95}>
      <lineSegments ref={linesRef} geometry={lineGeometry}>
        <lineBasicMaterial color="#ff6900" opacity={0.35} transparent />
      </lineSegments>
      <points ref={firingPointsRef} geometry={firingGeometry}>
        <pointsMaterial
          color="#ff6900"
          size={0.04}
          sizeAttenuation={true}
          transparent
          opacity={0.9}
        />
      </points>
    </group>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

// Client logos for marquee - desktop: big-name logos at positions 5-8 so they sit at the right edge on page load
const clientLogosDesktop = [
  { src: "/images/logos/crispin-seeklogo.png", alt: "Crispin" },
  { src: "/images/logos/Frontier-Airlines-Logo.png", alt: "Frontier Airlines" },
  { src: "/images/logos/Arbys-Logo.png", alt: "Arby's" },
  { src: "/images/logos/Applebees.png", alt: "Applebee's" },
  { src: "/images/logos/vail-resorts-seeklogo.png", alt: "Vail Resorts" },
  { src: "/images/logos/mcdonalds-logo.png", alt: "McDonald's" },
  { src: "/images/logos/Toyota-Black-Logo.png", alt: "Toyota" },
  { src: "/images/logos/American-Express-Logo-1.png", alt: "American Express" },
  { src: "/images/logos/goldman-sachs-logo.png", alt: "Goldman Sachs" },
  { src: "/images/logos/charles-schwab-logo.png", alt: "Charles Schwab" },
  { src: "/images/logos/SP-Global-Logo.png", alt: "S&P Global" },
  { src: "/images/logos/medtronic-healthcare-seeklogo.png", alt: "Medtronic" },
  { src: "/images/logos/Old-Navy.png", alt: "Old Navy" },
  { src: "/images/logos/CSU-1.png", alt: "Colorado State University" },
  { src: "/images/logos/Colorado-Logo-2013.png", alt: "State of Colorado" },
  { src: "/images/logos/covidien-seeklogo.png", alt: "Covidien" },
  { src: "/images/logos/arrow-electronics-logo.png", alt: "Arrow Electronics" },
  { src: "/images/logos/Fruit-of-the-Loom-Logo.png", alt: "Fruit of the Loom" },
  { src: "/images/logos/PIMCO-Logo.png", alt: "PIMCO" },
  { src: "/images/logos/Aspen-One-Logo-Black.webp", alt: "Aspen Skiing Company" },
];

// Client logos for mobile - biggest logos near front so they're visible sooner
const clientLogosMobile = [
  { src: "/images/logos/crispin-seeklogo.png", alt: "Crispin" },
  { src: "/images/logos/mcdonalds-logo.png", alt: "McDonald's" },
  { src: "/images/logos/Toyota-Black-Logo.png", alt: "Toyota" },
  { src: "/images/logos/American-Express-Logo-1.png", alt: "American Express" },
  { src: "/images/logos/goldman-sachs-logo.png", alt: "Goldman Sachs" },
  { src: "/images/logos/medtronic-healthcare-seeklogo.png", alt: "Medtronic" },
  { src: "/images/logos/charles-schwab-logo.png", alt: "Charles Schwab" },
  { src: "/images/logos/SP-Global-Logo.png", alt: "S&P Global" },
  { src: "/images/logos/CSU-1.png", alt: "Colorado State University" },
  { src: "/images/logos/Colorado-Logo-2013.png", alt: "State of Colorado" },
  { src: "/images/logos/covidien-seeklogo.png", alt: "Covidien" },
  { src: "/images/logos/Arbys-Logo.png", alt: "Arby's" },
  { src: "/images/logos/Applebees.png", alt: "Applebee's" },
  { src: "/images/logos/Frontier-Airlines-Logo.png", alt: "Frontier Airlines" },
  { src: "/images/logos/vail-resorts-seeklogo.png", alt: "Vail Resorts" },
  { src: "/images/logos/Old-Navy.png", alt: "Old Navy" },
  { src: "/images/logos/arrow-electronics-logo.png", alt: "Arrow Electronics" },
  { src: "/images/logos/Fruit-of-the-Loom-Logo.png", alt: "Fruit of the Loom" },
  { src: "/images/logos/PIMCO-Logo.png", alt: "PIMCO" },
  { src: "/images/logos/Aspen-One-Logo-Black.webp", alt: "Aspen Skiing Company" },
];

// Logo marquee component
function LogoMarquee({ heroComplete }: { heroComplete: boolean }) {
  return (
    <section
      className="relative z-20 py-6 md:py-12 border-y border-[#1a1a1a]/5 bg-white/50 backdrop-blur-sm overflow-hidden transition-all duration-1000"
      style={{
        opacity: heroComplete ? 1 : 0,
        transform: heroComplete ? 'translateY(0)' : 'translateY(20px)',
      }}
    >
      <div className="max-w-6xl mx-auto px-6 mb-6 md:mb-8 text-left md:text-center">
        <span className="text-sm text-[#ff6900] font-medium">Trusted By</span>
        <h2 className="hidden md:block text-2xl md:text-3xl font-semibold tracking-tight mt-2">Industry leaders we&apos;ve worked with</h2>
      </div>
      <div className="relative max-w-6xl mx-auto overflow-hidden">
        {/* Gradient fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

        {/* Mobile scrolling container - big logos near front */}
        <div
          className="flex md:hidden w-max hover:[animation-play-state:paused]"
          style={{ animation: 'marquee 55s linear infinite' }}
        >
          {/* First set of logos */}
          {clientLogosMobile.map((logo, index) => (
            <div
              key={`logo-mobile-1-${index}`}
              className="flex-shrink-0 mx-4 flex items-center justify-center w-28 h-10"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ))}
          {/* Duplicate set for seamless loop */}
          {clientLogosMobile.map((logo, index) => (
            <div
              key={`logo-mobile-2-${index}`}
              className="flex-shrink-0 mx-4 flex items-center justify-center w-28 h-10"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ))}
        </div>

        {/* Desktop scrolling container - big logos in middle */}
        <div
          className="hidden md:flex w-max hover:[animation-play-state:paused]"
          style={{ animation: 'marquee 55s linear infinite' }}
        >
          {/* First set of logos */}
          {clientLogosDesktop.map((logo, index) => (
            <div
              key={`logo-desktop-1-${index}`}
              className="flex-shrink-0 mx-6 flex items-center justify-center w-32 h-12"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ))}
          {/* Duplicate set for seamless loop */}
          {clientLogosDesktop.map((logo, index) => (
            <div
              key={`logo-desktop-2-${index}`}
              className="flex-shrink-0 mx-6 flex items-center justify-center w-32 h-12"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const workImages = [
  { src: "/images/work/mcdonalds-wwc-carousel.png", title: "McDonald's", description: "Interactive 3D iPad app tracing global potato supply chain", href: "/work/mcdonalds-wwc", objectPosition: "center" },
  { src: "/images/aspen-snowmass.jpg", title: "Aspen One", description: "Guest account portal and digital check-in platform", href: "/work/aspen-one", objectPosition: "center top" },
  { src: "/images/work/simplot-advisor.jpg", title: "J.R. Simplot Company", description: "Simplot Advisor platform for precision agriculture", href: "/work/simplot", objectPosition: "25% center" },
  { src: "/images/work/Alterra-2-new.jpg", title: "Alterra Mountain", description: "Ikon Pass resort tools application", href: "/work/alterra-mountain-company" },
  { src: "/images/success-stories/Guidehouse-Overview.jpg", title: "Guidehouse", description: "Webby-nominated Sitecore CMS migration", href: "/success-story/guidehouse" },
  { src: "/images/work/hexa-custom-overview.jpg", title: "Hexa Custom", description: "Custom apparel ordering platform", href: "/success-story/hexa-custom" },
  { src: "/images/work/aspen-one-mobile.jpg", title: "Aspen One", description: "Native iOS and Android resort mobile app", href: "/work/aspen-one" },
  { src: "/images/work/simplot-foods-contentful.jpg", title: "Simplot Foods", description: "Contentful CMS platform for foodservice products", href: "/work/simplot" },
  { src: "/images/work/jackson-hole-data-integration.avif", title: "Jackson Hole", description: "JH Insider mobile app experience", href: "/success-story/jackson-hole" },
  { src: "/images/work/portal-hero-carousel.png", title: "Portal Thermaculture", description: "End-to-end operations platform supporting bookings, memberships, and wellness tracking", href: "/work/portal-thermaculture", objectFit: "contain" as const, bgColor: "#1a1a1a" },
];

// Work showcase
function WorkShowcase({ heroComplete }: { heroComplete: boolean }) {
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollCarousel = (direction: "left" | "right") => {
    if (carouselRef.current) {
      const firstSlide = carouselRef.current.querySelector('a');
      if (firstSlide) {
        const slideWidth = firstSlide.offsetWidth;
        const gap = 40; // gap-10 = 2.5rem = 40px
        const scrollAmount = slideWidth + gap;
        carouselRef.current.scrollBy({
          left: direction === "left" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <section
      className="relative z-20 bg-white pt-20 md:pt-32 pb-20 md:pb-32 transition-all duration-1000"
      style={{
        opacity: heroComplete ? 1 : 0,
        transform: heroComplete ? 'translateY(0)' : 'translateY(30px)',
        filter: heroComplete ? 'blur(0)' : 'blur(8px)',
      }}
    >
      <div className="max-w-6xl mx-auto px-6 mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-sm text-[#ff6900] font-medium mb-2 block">Our Work</span>
            <h2 className="text-3xl font-semibold tracking-tight">Featured Projects</h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Navigation arrows */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => scrollCarousel("left")}
                className="w-10 h-10 rounded-full border border-[#1a1a1a]/20 bg-white flex items-center justify-center hover:bg-[#1a1a1a]/5 hover:border-[#1a1a1a]/30 transition-colors shadow-sm cursor-pointer"
                aria-label="Previous project"
              >
                <svg className="w-4 h-4 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => scrollCarousel("right")}
                className="w-10 h-10 rounded-full border border-[#1a1a1a]/20 bg-white flex items-center justify-center hover:bg-[#1a1a1a]/5 hover:border-[#1a1a1a]/30 transition-colors shadow-sm cursor-pointer"
                aria-label="Next project"
              >
                <svg className="w-4 h-4 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <Link
              href="/work"
              className="text-sm text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors flex items-center gap-1"
            >
              View all
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <div
        ref={carouselRef}
        className="flex gap-10 pb-8 overflow-x-auto scrollbar-hide cursor-pointer"
        style={{ paddingLeft: 'max(1.5rem, calc((100vw - 72rem) / 2 + 1.5rem))' }}
      >
        {workImages.map((image, index) => (
          <Link
            key={index}
            href={image.href}
            className="flex-shrink-0 w-[calc(100vw-3rem)] md:w-[750px] lg:w-[900px] group"
          >
            <div className="aspect-[16/10] rounded-2xl overflow-hidden shadow-2xl shadow-black/10 mb-4" style={{ backgroundColor: image.bgColor || 'rgba(26,26,26,0.05)' }}>
              <img
                src={image.src}
                alt={image.title}
                className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${image.objectFit === 'contain' ? 'object-contain' : 'object-cover'}`}
                style={{ objectPosition: image.objectPosition || 'center' }}
              />
            </div>
            <h3 className="font-semibold text-lg mb-1 group-hover:text-[#ff6900] transition-colors">
              {image.title}
            </h3>
            <p className="text-[#1a1a1a]/70 text-sm">
              {image.description}
            </p>
          </Link>
        ))}
        {/* Spacer for right edge */}
        <div className="flex-shrink-0 w-6" />
      </div>
    </section>
  );
}

const services = [
  {
    title: "AI-Native Development",
    description: "We build enterprise software with AI embedded into both the product and the delivery process, reducing cycle time while maintaining quality, security, and maintainability.",
    href: "/services/full-stack-web-development",
    image: "/images/services/homepage-service-1.jpg",
  },
  {
    title: "Data Services",
    description: "We modernize data platforms so teams can trust their data, scale analytics, and confidently support AI-driven use cases without rebuilding foundations later.",
    href: "/services/data-services",
    image: "/images/services/data.jpg",
  },
  {
    title: "UX/UI Design",
    description: "We design interfaces and design systems that move cleanly from concept to code, reducing handoffs between teams and accelerating delivery without sacrificing usability.",
    href: "/services/ux-ui-design",
    image: "/images/services/homepage-service-2.jpg",
  },
  {
    title: "Content Management",
    description: "We implement modern CMS and headless platforms that integrate cleanly with enterprise systems, enabling teams to manage content at scale without operational friction.",
    href: "/services/content-management",
    image: "/images/services/content-management.jpg",
  },
];

export default function Home() {
  const [glowPos, setGlowPos] = useState({ x: 0, y: 0 });
  const targetPos = useRef({ x: 0, y: 0 });

  // Typewriter state lifted to control multiple sections
  const heroFullText = "AI-native software\ndelivery for\nmodern enterprises";
  const { displayedText: heroDisplayedText, isComplete: heroComplete } = useTypewriter(heroFullText, 40, 500);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Convert to centered coordinates (-1 to 1)
      targetPos.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      targetPos.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    let animationId: number;
    const currentPos = { x: 0, y: 0 };

    const animate = () => {
      // Lerp towards target
      currentPos.x += (targetPos.current.x - currentPos.x) * 0.03;
      currentPos.y += (targetPos.current.y - currentPos.y) * 0.03;
      setGlowPos({ x: currentPos.x, y: currentPos.y });
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden [&_.scrollbar-hide]:[-ms-overflow-style:none] [&_.scrollbar-hide]:[scrollbar-width:none] [&_.scrollbar-hide::-webkit-scrollbar]:hidden">
      {/* Three.js Canvas Background */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        {/* Hero Section */}
        <HeroSection displayedText={heroDisplayedText} isComplete={heroComplete} />

        {/* Client Logo Marquee */}
        <LogoMarquee heroComplete={heroComplete} />

        {/* Work showcase - horizontal scroll, peeking above the fold */}
        <WorkShowcase heroComplete={heroComplete} />

        {/* Clients bar */}
        <section className="py-12 md:py-20 px-6 border-y border-[#1a1a1a]/5 bg-white/50 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-sm text-[#ff6900] font-medium mb-2 block">Our Technology</span>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
                Built on the platforms that power modern enterprises
              </h2>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-6 md:gap-8 text-[#1a1a1a]">
              {[
                { src: "/images/tech/anthropic.svg", alt: "Anthropic", scale: 0.95 },
                { src: "/images/tech/openai.svg", alt: "OpenAI", scale: 1.0 },
                { src: "/images/tech/microsoft.svg", alt: "Microsoft", scale: 0.85 },
                { src: "/images/tech/azure.svg", alt: "Microsoft Azure", scale: 1.0 },
                { src: "/images/tech/aws.svg", alt: "AWS", scale: 1.15 },
                { src: "/images/tech/apple.svg", alt: "Apple", scale: 1.05 },
                { src: "/images/tech/react.svg", alt: "React", scale: 1.05 },
                { src: "/images/tech/nodejs.svg", alt: "Node.js", scale: 1.0 },
                { src: "/images/tech/nextjs.svg", alt: "Next.js", scale: 1.0 },
                { src: "/images/tech/sitecore.svg", alt: "Sitecore", scale: 1.0 },
                { src: "/images/tech/contentstack.png", alt: "Contentstack", scale: 1.4 },
                { src: "/images/tech/android.svg", alt: "Android", scale: 1.05 },
              ].map((logo, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center h-12"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    title={logo.alt}
                    style={{ transform: `scale(${logo.scale})` }}
                    className="h-10 w-10 object-contain brightness-0 opacity-50 hover:opacity-80 transition-opacity"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-12 md:py-20 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-16 mb-10 md:mb-20">
              <div className="lg:col-span-5">
                <span className="text-sm text-[#ff6900] font-medium mb-4 block">Services</span>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
                  What we do
                </h2>
              </div>
              <div className="lg:col-span-7 flex items-end">
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed max-w-xl">
                  End-to-end capabilities for enterprise software delivery, from strategy and design through build and launch.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a1a1a]/5 rounded-2xl overflow-hidden">
              {services.map((service, index) => (
                <Link
                  key={index}
                  href={service.href}
                  className="group relative bg-[#fafafa] p-10 transition-all duration-500 overflow-hidden"
                >
                  {/* Background image with overlay - shows on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ backgroundImage: `url(${service.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                  >
                    <div className="absolute inset-0 bg-[#1a1a1a]/80" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <div className="mb-6">
                      <span className="text-sm text-[#1a1a1a]/20 group-hover:text-white/40 font-mono transition-colors duration-500">
                        0{index + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-white transition-colors duration-500">
                      {service.title}
                    </h3>
                    <p className="text-[#1a1a1a]/70 group-hover:text-white/70 leading-relaxed mb-4 transition-colors duration-500">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-[#ff6900] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                      Learn More
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-16 md:py-24 px-6 bg-white/50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm text-[#ff6900] font-medium mb-2 block">Why Mapleton Hill</span>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
                What sets us apart
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "Enterprise platforms", description: "Complex platforms for regulated, large-scale organizations" },
                { title: "Long-term partnerships", description: "Multi-year partnerships built on trust and delivery" },
                { title: "Product delivery", description: "From strategy and design through build and launch" },
                { title: "Senior-led teams", description: "Senior-led, hands-on, and deeply integrated with clients" },
              ].map((item, index) => (
                <div key={index} className="group cursor-default">
                  <div className="text-6xl font-bold text-[#ff6900]/10 group-hover:text-[#ff6900] transition-colors duration-300 mb-4">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-[#1a1a1a]/70 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to build something?
            </h2>
            <p className="text-xl text-[#1a1a1a]/70 mb-10 max-w-xl mx-auto">
              Let&apos;s discuss how AI-native development can accelerate your next project.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white font-medium hover:opacity-90 transition-opacity"
            >
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        </main>

        <Footer />
      </div>
    </div>
  );
}
